import React, { useState, useEffect } from 'react';
import { siteData } from '../data/mock';

const INTEREST_BASELINE = 189;
const INTEREST_CLICKED_KEY = 'demoboy_interest_clicked';

const LastPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [interestLoading, setInterestLoading] = useState(null);
  const [interestClicked, setInterestClicked] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [interestCount, setInterestCount] = useState(INTEREST_BASELINE);
  const cta = siteData.cta;

  // Fetch initial waitlist count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const response = await fetch(`/api/waitlist/count`);
        if (response.ok) {
          const data = await response.json();
          setWaitlistCount(data.count);
        }
      } catch (err) {
        console.error('Error fetching waitlist count:', err);
      }
    };
    fetchCount();
  }, []);

  // Fetch initial interest counters
  useEffect(() => {
    const fetchInterest = async () => {
      try {
        setInterestClicked(localStorage.getItem(INTEREST_CLICKED_KEY) === 'true');
        const response = await fetch(`/api/interest/count`);
        if (response.ok) {
          const data = await response.json();
          setInterestCount(Math.max(INTEREST_BASELINE, Number(data.interested || 0)));
        }
      } catch (err) {
        // Non-blocking
      }
    };
    fetchInterest();
  }, []);

  const handleInterestClick = async () => {
    if (localStorage.getItem(INTEREST_CLICKED_KEY) === 'true') {
      setInterestClicked(true);
      setMessage('');
      setError('unable to fulfill the request');
      return;
    }

    setInterestLoading('interested');
    setInterestClicked(true);
    localStorage.setItem(INTEREST_CLICKED_KEY, 'true');
    setMessage('');
    setError('');

    // Optimistic update
    setInterestCount((prev) => prev + 1);

    try {
      const response = await fetch(`/api/interest/click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'interested' }),
      });

      // Even if the request fails (storage not configured), keep the optimistic UI.
      if (response.ok) {
        const data = await response.json();
        setInterestCount(Math.max(INTEREST_BASELINE, Number(data.interested || 0)));
      } else {
        // Best-effort resync (non-blocking)
        fetch(`/api/interest/count`)
          .then((r) => (r.ok ? r.json() : null))
          .then((d) => {
            if (d && typeof d.interested !== 'undefined') {
              setInterestCount(Math.max(INTEREST_BASELINE, Number(d.interested || 0)));
            }
          })
          .catch(() => {});
      }
    } catch (err) {
      // Keep optimistic UI; try to resync silently.
      fetch(`/api/interest/count`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && typeof d.interested !== 'undefined') {
            setInterestCount(Math.max(INTEREST_BASELINE, Number(d.interested || 0)));
          }
        })
        .catch(() => {});
    } finally {
      setInterestLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`/api/waitlist/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('🎉 Welcome to the waitlist!');
        setEmail('');
        // Update count from response
        if (data.total_count) {
          setWaitlistCount(data.total_count);
        }
      } else if (response.status === 409 || response.status === 422) {
        setError(data.detail || 'This email is already on our waitlist!');
      } else {
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error('Error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-section="last-page"
      className="relative w-full lastpage"
      style={{ background: '#fafafa', overflow: 'hidden' }}
    >
      {/* Grid pattern background */}
      <div
        style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${process.env.PUBLIC_URL}/assets/grid-pattern.png)`,
          backgroundRepeat: 'repeat',
          backgroundSize: '242px 242px',
          opacity: 0.25,
          pointerEvents: 'none', zIndex: 0,
        }}
      />

      {/* Main content — two column with equal heights */}
      <div
        className="relative lastpage-layout"
        style={{ zIndex: 1 }}
      >
        {/* Left side — 3D Video (matches card height) */}
        <div
          className="lastpage-media"
        >
          <div className="lastpage-mediaInner">
            <video
              autoPlay loop muted playsInline
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
                borderRadius: '12px',
              }}
            >
              <source src="/assets/3dsvg-video.mp4" type="video/mp4" />
            </video>
          </div>
        </div>

        {/* Right side — Signup Card */}
        <div
          className="lastpage-cardWrap"
        >
          <div
            className="lastpage-card"
          >
            {/* Heading — bold, large */}
            <h2
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: '36px',
                fontWeight: 400,
                color: '#000',
                letterSpacing: '-0.03em',
                lineHeight: '38px',
                marginBottom: '6px',
              }}
            >
              {cta.heading}
            </h2>

            {/* Subtitle */}
            <p
              style={{
                fontFamily: 'InterVariable, sans-serif',
                fontSize: '18px',
                fontWeight: 400,
                fontStyle: 'italic',
                color: '#000',
                letterSpacing: '-0.02em',
                lineHeight: '22px',
                marginBottom: '40px',
              }}
            >
              {cta.subtitle}
            </p>

            {/* Get early access */}
            <p
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: '18px',
                fontWeight: 400,
                color: '#000',
                letterSpacing: '-0.02em',
                lineHeight: '20px',
                marginBottom: '14px',
                textAlign: 'center',
              }}
            >
              {cta.buttonText}
            </p>

            {/* Email input */}
            <form onSubmit={handleSubmit} style={{ marginBottom: '28px' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="hermoine.granger@hogwarts.com"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '14px 20px',
                  fontFamily: 'InterVariable, sans-serif',
                  fontSize: '16px',
                  fontWeight: 400,
                  fontStyle: 'italic',
                  color: '#000',
                  letterSpacing: '-0.02em',
                  lineHeight: '22px',
                  border: error ? '1.5px solid #FF0000' : '1.5px solid #FF98BF',
                  borderRadius: '8px',
                  outline: 'none',
                  background: 'transparent',
                }}
              />

              {/* Waitlist count */}
              {waitlistCount > 0 && (
                <div style={{ textAlign: 'center', marginTop: '14px' }}>
                  <p
                    style={{
                      fontFamily: 'InterVariable, sans-serif',
                      fontSize: '13px',
                      color: '#666',
                      fontStyle: 'italic',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: '#FF98BF' }}>
                      {waitlistCount}
                    </span>{' '}
                    people on the waitlist
                  </p>
                </div>
              )}

              {/* Interest button */}
              <div style={{ marginTop: '14px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleInterestClick}
                  disabled={interestLoading !== null}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontFamily: 'InterVariable, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: '#000',
                    letterSpacing: '-0.02em',
                    border: '1.5px solid #FF98BF',
                    borderRadius: '10px',
                    background: interestClicked ? 'rgba(255,152,191,0.10)' : 'transparent',
                    boxShadow: interestClicked
                      ? '0 0 0 3px rgba(255,152,191,0.35), 0 0 24px rgba(255,152,191,0.30)'
                      : 'none',
                    transition: 'box-shadow 180ms ease, background 180ms ease, opacity 180ms ease',
                    cursor: interestLoading !== null ? 'not-allowed' : 'pointer',
                    opacity: interestLoading !== null ? 0.6 : 1,
                  }}
                >
                  i am interested
                </button>

                <div
                  style={{
                    fontFamily: 'InterVariable, sans-serif',
                    fontSize: '12px',
                    fontStyle: 'italic',
                    color: '#000',
                    marginTop: '10px',
                    opacity: 0.75,
                  }}
                >
                  {interestCount} {interestCount === 1 ? 'person is' : 'people are'} interested
                </div>
              </div>

              {/* Status messages */}
              {error && (
                <p style={{
                  fontFamily: 'InterVariable, sans-serif',
                  fontSize: '14px',
                  color: '#FF0000',
                  marginTop: '8px',
                  fontStyle: 'italic',
                }}>
                  {error}
                </p>
              )}
              
              {message && (
                <p style={{
                  fontFamily: 'InterVariable, sans-serif',
                  fontSize: '14px',
                  color: '#00AA00',
                  marginTop: '8px',
                  fontStyle: 'italic',
                }}>
                  {message}
                </p>
              )}
              
              {/* Hidden submit button - form submits on Enter */}
              <button type="submit" style={{ display: 'none' }} />
            </form>

            {/* Extras section */}
            <div>
              <p
                style={{
                  fontFamily: 'Coolvetica, sans-serif',
                  fontSize: '18px',
                  fontWeight: 400,
                  color: '#000',
                  letterSpacing: '-0.02em',
                  lineHeight: '22px',
                  marginBottom: '8px',
                }}
              >
                {cta.extras.title}
              </p>
              {cta.extras.items.map((item, index) => (
                <p
                  key={index}
                  style={{
                    fontFamily: 'InterVariable, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    color: '#000',
                    letterSpacing: '-0.02em',
                    lineHeight: '22px',
                  }}
                >
                  {item}
                </p>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Removed bottom grid spacer to eliminate white space before footer */}
    </section>
  );
};

export default LastPage;
